var _a;
import { __assign, __awaiter, __generator } from "tslib";
import { SearchApi } from '../api';
import { DocumentParser, SuggestionsParser, parseDocumentTypes, parseTotals, } from './parsers';
import { SearchDocumentType, } from './sdk.types';
import MockAdapter from 'axios-mock-adapter';
import { mockApi } from './mockApi';
function getSearchApiDocumentType(documentType) {
    if (documentType === SearchDocumentType.All) {
        return;
    }
    return documentType;
}
function getQuery(query, forcePartialMatch) {
    return query ? "" + query + (forcePartialMatch ? '*' : '') : '*';
}
var ClientSearchSDK = /** @class */ (function () {
    function ClientSearchSDK(config) {
        this.language = config.language;
        this.forcePartialMatch = config.forcePartialMatch || false;
        this.api = new SearchApi({
            token: config.token,
            baseURL: config.baseURL,
            experimental: config.experimental,
        });
        this.apiMockAdapter = null;
        this.documentParser = new DocumentParser(config.siteBaseURL);
        this.suggestionsParser = new SuggestionsParser(config.siteBaseURL, config.language);
    }
    ClientSearchSDK.getDocumentTypeOrderIndex = function (documentType) {
        var index = ClientSearchSDK.documentTypesOrder[documentType.documentType];
        return index === undefined ? Number.MAX_SAFE_INTEGER : index;
    };
    ClientSearchSDK.documentTypesComparator = function (a, b) {
        return (ClientSearchSDK.getDocumentTypeOrderIndex(a) -
            ClientSearchSDK.getDocumentTypeOrderIndex(b));
    };
    ClientSearchSDK.prototype.setToken = function (token) {
        this.api.setToken(token);
    };
    ClientSearchSDK.prototype.getInstance = function () {
        return this.api.getInstance();
    };
    ClientSearchSDK.prototype.getDocumentTypes = function () {
        return __awaiter(this, void 0, void 0, function () {
            var response;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, this.api.getDocumentTypes()];
                    case 1:
                        response = _a.sent();
                        // NOTE: DocumentType.ALL is added at `parseDocumentTypes` if there is more than 1 document type
                        return [2 /*return*/, parseDocumentTypes(response.data.sources).sort(ClientSearchSDK.documentTypesComparator)];
                }
            });
        });
    };
    ClientSearchSDK.prototype.getTotals = function (searchRequest) {
        return __awaiter(this, void 0, void 0, function () {
            var response;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, this.api.search(__assign(__assign({}, searchRequest), { documentType: undefined, language: this.language, query: getQuery(searchRequest.query, this.forcePartialMatch), paging: {
                                skip: 0,
                                limit: 0,
                            } }))];
                    case 1:
                        response = _a.sent();
                        return [2 /*return*/, parseTotals(response.data)];
                }
            });
        });
    };
    ClientSearchSDK.prototype.search = function (searchRequest) {
        return __awaiter(this, void 0, void 0, function () {
            var response;
            var _this = this;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, this.api.search(__assign(__assign({}, searchRequest), { documentType: getSearchApiDocumentType(searchRequest.documentType), query: getQuery(searchRequest.query, this.forcePartialMatch), language: this.language, paging: searchRequest.paging && {
                                skip: (searchRequest.paging.page - 1) * searchRequest.paging.pageSize,
                                limit: searchRequest.paging.pageSize,
                            } }))];
                    case 1:
                        response = _a.sent();
                        return [2 /*return*/, {
                                documents: response.data.documents.map(function (document) {
                                    return _this.documentParser.parse(document);
                                }),
                                totalResults: response.data.nextPage ? response.data.nextPage.total : 0,
                            }];
                }
            });
        });
    };
    ClientSearchSDK.prototype.useDemoContent = function (options) {
        if (!this.apiMockAdapter) {
            this.apiMockAdapter = new MockAdapter(this.api.getInstance());
        }
        else {
            this.apiMockAdapter.reset();
        }
        mockApi(this.apiMockAdapter, options);
    };
    ClientSearchSDK.prototype.getSuggestions = function (suggestionsRequest) {
        return __awaiter(this, void 0, void 0, function () {
            var response;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, this.api.getSuggestions({
                            query: suggestionsRequest.query || '*',
                            limit: suggestionsRequest.limit || 10,
                            language: this.language,
                        })];
                    case 1:
                        response = _a.sent();
                        return [2 /*return*/, {
                                suggestions: this.suggestionsParser.parse(response.data.documents),
                            }];
                }
            });
        });
    };
    ClientSearchSDK.prototype.getSample = function (sampleRequest) {
        return __awaiter(this, void 0, void 0, function () {
            var response, documentsMapper, samples;
            var _this = this;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, this.api.getSample(__assign(__assign({}, sampleRequest), { 
                            // TODO: check if `*` is required
                            // query: sampleRequest.query || '*',
                            language: this.language }))];
                    case 1:
                        response = _a.sent();
                        documentsMapper = function (document) {
                            return _this.documentParser.parse(document);
                        };
                        samples = response.data.samples
                            .sort(ClientSearchSDK.documentTypesComparator)
                            .map(function (sample) { return (__assign(__assign({}, sample), { documents: sample.documents.map(documentsMapper) })); });
                        return [2 /*return*/, {
                                samples: samples,
                            }];
                }
            });
        });
    };
    ClientSearchSDK.prototype.onError = function (onError) {
        var axiosInstance = this.api.getInstance();
        var onFulfilled = function (config) { return config; };
        var onRejected = function (error) {
            onError(error);
            return Promise.reject(error);
        };
        axiosInstance.interceptors.request.use(onFulfilled, onRejected);
        axiosInstance.interceptors.response.use(onFulfilled, onRejected);
    };
    ClientSearchSDK.documentTypesOrder = (_a = {},
        _a[SearchDocumentType.All] = 0,
        _a[SearchDocumentType.Pages] = 1,
        _a[SearchDocumentType.Products] = 2,
        _a[SearchDocumentType.Blogs] = 3,
        _a[SearchDocumentType.Forums] = 4,
        _a[SearchDocumentType.Bookings] = 5,
        _a);
    return ClientSearchSDK;
}());
export { ClientSearchSDK };
//# sourceMappingURL=sdk.js.map